/**
 * StorageSlot - Type-safe storage abstraction
 *
 * Based on Martin Fowler's "Hide Delegate" and "Encapsulate Field" patterns
 * Provides centralized, type-safe access to localStorage/sessionStorage.
 *
 * Sprint B (Master Plan v5.1) — TENANT ISOLATION:
 *   Keys are prefixed with `t:<tenantSlug>:` by default. Without a resolved
 *   tenant slug (`window.__TENANT_SLUG__`), writes throw and reads return null.
 *   This prevents tenant-A's auth_token / cart / draft from leaking into
 *   tenant-B's session on the same browser host.
 *
 *   Use `tenantScope: 'global'` to opt out for genuinely cross-tenant data
 *   (UI language preference, PWA service-worker scope). Default is 'tenant'.
 *
 *   Non-negotiable rule #2: NEVER write to `t:unknown:*`. Without slug, fail
 *   loud on writes — silent fallback creates a class of "where did my cart go"
 *   bugs after bootstrap completes.
 */

import { isBrowser } from './constants'

export interface StorageSlotOptions<T> {
  /** Time-to-live in milliseconds. If set, values expire after this duration */
  ttl?: number
  /** Storage type: 'local' for localStorage, 'session' for sessionStorage */
  storage?: 'local' | 'session'
  /** Custom validation function. Returns true if value is valid */
  validate?: (value: T) => boolean
  /** Custom serialization function */
  serialize?: (value: T) => string
  /** Custom deserialization function */
  deserialize?: (raw: string) => T
  /** Default value to return when storage is empty or invalid */
  defaultValue?: T
  /**
   * Tenant scoping mode (Sprint B).
   * - 'tenant' (default): key is prefixed with `t:<slug>:` from window.__TENANT_SLUG__.
   *   Writes throw without slug; reads return null.
   * - 'global': key used verbatim. ONLY for genuinely cross-tenant data
   *   (UI language preference). Anything user-specific MUST be 'tenant'.
   */
  tenantScope?: 'tenant' | 'global'
}

interface StoredValue<T> {
  value: T
  timestamp: number
  version?: number
}

/**
 * Generic storage slot for type-safe localStorage/sessionStorage operations
 *
 * @example
 * const authToken = new StorageSlot<string>('customer_token')
 * authToken.set('my-token')
 * const token = authToken.get() // string | null
 *
 * @example
 * const deliverySlot = new StorageSlot<DeliverySlot>('delivery-slot', {
 *   ttl: 30 * 60 * 1000, // 30 minutes
 *   validate: (slot) => !!slot.date && !!slot.time
 * })
 */
export class StorageSlot<T> {
  private readonly key: string
  private readonly options: StorageSlotOptions<T>

  constructor(key: string, options: StorageSlotOptions<T> = {}) {
    this.key = key
    this.options = {
      storage: 'local',
      tenantScope: 'tenant',
      ...options
    }
  }

  /**
   * Get the storage instance (localStorage or sessionStorage)
   */
  private getStorage(): Storage | null {
    if (!isBrowser) return null
    return this.options.storage === 'session' ? sessionStorage : localStorage
  }

  /**
   * Resolve effective key with tenant prefix (Sprint B).
   * For tenantScope='global', returns raw key.
   * For tenantScope='tenant':
   *   - On write, throws if no slug (rule #2 — never `t:unknown:*`).
   *   - On read, returns null if no slug (caller gets defaultValue).
   */
  private getEffectiveKey(mode: 'read' | 'write'): string | null {
    if (this.options.tenantScope === 'global') return this.key

    const slug = typeof window !== 'undefined' ? window.__TENANT_SLUG__ : undefined

    if (!slug) {
      if (mode === 'write') {
        throw new Error(
          `[StorageSlot] Cannot write tenant-scoped key '${this.key}' — ` +
          `window.__TENANT_SLUG__ is not set. Wait for TenantProvider to bootstrap, ` +
          `or pass { tenantScope: 'global' } if this key is genuinely cross-tenant.`
        )
      }
      return null
    }

    return `t:${slug}:${this.key}`
  }

  /**
   * Get value from storage
   * Returns null if not found, expired, or invalid
   */
  get(): T | null {
    const storage = this.getStorage()
    if (!storage) return this.options.defaultValue ?? null

    const effectiveKey = this.getEffectiveKey('read')
    if (!effectiveKey) return this.options.defaultValue ?? null

    try {
      const raw = storage.getItem(effectiveKey)
      if (!raw) return this.options.defaultValue ?? null

      // Check if we need to handle TTL (stored with metadata)
      if (this.options.ttl) {
        const stored: StoredValue<T> = JSON.parse(raw)

        // Check expiration
        if (Date.now() - stored.timestamp > this.options.ttl) {
          this.remove()
          return this.options.defaultValue ?? null
        }

        const value = stored.value

        // Validate if validator provided
        if (this.options.validate && !this.options.validate(value)) {
          return this.options.defaultValue ?? null
        }

        return value
      }

      // Simple storage without TTL
      const value = this.options.deserialize
        ? this.options.deserialize(raw)
        : JSON.parse(raw) as T

      // Validate if validator provided
      if (this.options.validate && !this.options.validate(value)) {
        return this.options.defaultValue ?? null
      }

      return value
    } catch (error) {
      console.warn(`[Storage] Failed to read "${effectiveKey}":`, error)
      return this.options.defaultValue ?? null
    }
  }

  /**
   * Set value in storage
   */
  set(value: T): void {
    const storage = this.getStorage()
    if (!storage) return

    // getEffectiveKey throws on tenant-scoped write without slug.
    // We let it propagate — silent skip would create the bug class
    // we are explicitly preventing (rule #2).
    const effectiveKey = this.getEffectiveKey('write')
    if (!effectiveKey) return

    try {
      // Validate before saving
      if (this.options.validate && !this.options.validate(value)) {
        console.warn(`[Storage] Validation failed for "${effectiveKey}", not saving`)
        return
      }

      let serialized: string

      if (this.options.ttl) {
        // Store with metadata for TTL
        const stored: StoredValue<T> = {
          value,
          timestamp: Date.now()
        }
        serialized = JSON.stringify(stored)
      } else {
        // Simple storage
        serialized = this.options.serialize
          ? this.options.serialize(value)
          : JSON.stringify(value)
      }

      storage.setItem(effectiveKey, serialized)
    } catch (error) {
      console.error(`[Storage] Failed to write "${effectiveKey}":`, error)
    }
  }

  /**
   * Remove value from storage
   */
  remove(): void {
    const storage = this.getStorage()
    if (!storage) return

    const effectiveKey = this.getEffectiveKey('read')
    if (!effectiveKey) return

    try {
      storage.removeItem(effectiveKey)
    } catch (error) {
      console.warn(`[Storage] Failed to remove "${effectiveKey}":`, error)
    }
  }

  /**
   * Check if value exists in storage
   */
  has(): boolean {
    return this.get() !== null
  }

  /**
   * Check if value is still valid (not expired)
   * For slots without TTL, returns true if value exists
   */
  isValid(): boolean {
    if (!this.options.ttl) {
      return this.has()
    }

    const storage = this.getStorage()
    if (!storage) return false

    const effectiveKey = this.getEffectiveKey('read')
    if (!effectiveKey) return false

    try {
      const raw = storage.getItem(effectiveKey)
      if (!raw) return false

      const stored: StoredValue<T> = JSON.parse(raw)
      const isNotExpired = Date.now() - stored.timestamp <= this.options.ttl

      if (!isNotExpired) {
        this.remove()
        return false
      }

      // Also run validation
      if (this.options.validate) {
        return this.options.validate(stored.value)
      }

      return true
    } catch {
      return false
    }
  }

  /**
   * Get the raw string value from storage (for debugging/migration)
   */
  getRaw(): string | null {
    const storage = this.getStorage()
    if (!storage) return null
    const effectiveKey = this.getEffectiveKey('read')
    if (!effectiveKey) return null
    return storage.getItem(effectiveKey)
  }

  /**
   * Get remaining TTL in milliseconds
   * Returns null if no TTL, 0 if expired, or remaining time
   */
  getRemainingTTL(): number | null {
    if (!this.options.ttl) return null

    const storage = this.getStorage()
    if (!storage) return null

    const effectiveKey = this.getEffectiveKey('read')
    if (!effectiveKey) return 0

    try {
      const raw = storage.getItem(effectiveKey)
      if (!raw) return 0

      const stored: StoredValue<T> = JSON.parse(raw)
      const elapsed = Date.now() - stored.timestamp
      const remaining = this.options.ttl - elapsed

      return remaining > 0 ? remaining : 0
    } catch {
      return 0
    }
  }

  /**
   * Update value using a transform function
   * Useful for partial updates
   */
  update(transform: (current: T | null) => T | null): void {
    const current = this.get()
    const updated = transform(current)

    if (updated === null) {
      this.remove()
    } else {
      this.set(updated)
    }
  }

  /**
   * Get the storage key (for debugging/testing).
   * Returns the LOGICAL key (without tenant prefix).
   * Use getEffectiveKeyDebug() to inspect the actual storage key.
   */
  getKey(): string {
    return this.key
  }

  /**
   * Get the EFFECTIVE storage key (with tenant prefix, if applicable).
   * Read-mode (returns null without slug). For debugging only.
   */
  getEffectiveKeyDebug(): string | null {
    return this.getEffectiveKey('read')
  }
}

